import { KeyValue } from "@angular/common";
import { Inject, Injectable, Injector } from "@angular/core";
import { BaseEntity } from "@common/ADAPT.Common.Model/base-entity";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { Survey, SurveyType } from "@common/ADAPT.Common.Model/organisation/survey";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { UserService } from "@common/user/user.service";
import { DurationSelector } from "@common/ux/duration-selector";
import { ActivityPage, IActivitiesGroupedByActivityType, IActivitiesGroupedByStatusCounts, IActivityEntityType, IActivityGroup, ICalendarCulturalActivity, ICulturalActivitiesGroupedByStatusAndType, ICulturalActivityStatusGroupKeyValueRecord } from "@org-common/lib/activities/activities-metadata";
import { ACTIVITY_HANDLERS, ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { ActivityType, CulturalActivityMetadataByType, CulturalActivityTypeMetadata, CulturalActivityTypes, ICulturalActivityTypeMetadata, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { IObjectiveTeamGroup } from "@org-common/lib/objectives/objectives.service";
import { AfterOrganisationInitialisation } from "@org-common/lib/organisation/after-organisation-initialisation.decorator";
import { BaseOrganisationService } from "@org-common/lib/organisation/base-organisation.service";
import moment from "moment";
import { merge, Subject } from "rxjs";
import { IMeetingTeamGroup } from "../meetings/meetings.service";

@Injectable({
    providedIn: "root",
})
export class ActivitiesService extends BaseOrganisationService {
    private activityTypesLoaded = new Subject<ActivityType[]>();
    public activityTypesLoaded$ = this.activityTypesLoaded.asObservable();

    private activityTypesError = new Subject<{ types: ActivityType[], error: any }>();
    public activityTypesError$ = this.activityTypesError.asObservable();

    private accessibleActivityTypes?: ActivityType[];

    public constructor(
        injector: Injector,
        authNotificationService: AuthorisationNotificationService,
        private authorisationService: AuthorisationService,
        private userService: UserService,
        private featuresService: FeaturesService,
        private activityPageService: ActivityPageService,
        @Inject(ACTIVITY_HANDLERS) private activityHandlers: ActivityHandler[],
    ) {
        super(injector);

        // update accessibleActivityTypes when permissions change
        authNotificationService.authorisationChanged$.subscribe(() => this.initialiseService());
    }

    public get hasAnyActivityType() {
        return !this.accessibleActivityTypes || this.accessibleActivityTypes.length > 0;
    }

    public get shouldUpdate$() {
        return merge(...this.activityHandlers.map((h) => h.shouldUpdate$));
    }

    public static getActivityType(event: IActivityEntityType): ActivityType {
        if (event instanceof CulturalActivity) {
            return event.type;
        } else if (event instanceof Meeting) {
            return NonCulturalActivityType.Meetings;
        } else if (event instanceof Item) {
            return NonCulturalActivityType.Actions;
        } else {
            return NonCulturalActivityType.Objectives;
        }
    }

    public static getActivityTypeForSurvey(survey: Survey): ActivityType {
        switch (survey.surveyType) {
            case SurveyType.TeamAssessment:
                return CulturalActivityType.TeamAssessment;
            case SurveyType.ResilientBusinessAssessment:
                return CulturalActivityType.ResilientBusinessAssessment;
            case SurveyType.EmployeeEngagement:
                return CulturalActivityType.EmployeeEngagementAssessment;
            default:
                throw new Error(`Unknown survey type ${survey.surveyType}`);
        }
    }

    public static generateActivitiesGroupedByTypeObject(types?: ActivityType[]): IActivitiesGroupedByActivityType {
        types = types ?? CulturalActivityTypes;
        return Object.fromEntries<IActivitiesGroupedByActivityType[ActivityType]>(types.map((type) =>
            [type, ActivitiesService.generateActivitiesGroupObject()])) as IActivitiesGroupedByActivityType;
    }

    public static generateActivitiesGroupObject() {
        return {
            [CulturalActivityStatus.Overdue]: [],
            [CulturalActivityStatus.Upcoming]: [],
            allItems: [],
            displayedCount: 0,
            totalCount: 0,
            totalCountByStatus: this.generateActivitiesGroupedByStatusCountObject(),
        } as IActivitiesGroupedByActivityType[ActivityType];
    }

    public static generateActivitiesGroupedByStatusCountObject() {
        return {
            [CulturalActivityStatus.Overdue]: 0,
            [CulturalActivityStatus.Upcoming]: 0,
        } as IActivitiesGroupedByStatusCounts;
    }

    public static getKeyForActivityType(activity: IActivityEntityType) {
        // generic way to track any entity type
        // if it's not an entity, it's a group, so use the teamId to track
        return activity instanceof BaseEntity
            ? activity.entityAspect.getKey().toString()
            : ("objectives" in activity ? "objectives" : "meetings") + activity.team?.entityAspect.getKey().toString();
    }

    @AfterOrganisationInitialisation
    public async getAccessibleActivityTypes() {
        return [...this.accessibleActivityTypes!];
    }

    public isActivityTypeVisible(section: ICulturalActivityTypeMetadata) {
        return !!(this.accessibleActivityTypes?.includes(section.type) && this.activityPageService.filter?.visibleTypes?.includes(section));
    }

    public isActivityStatusVisible(status: CulturalActivityStatus) {
        return !!this.activityPageService.filter?.visibleStatuses?.includes(status);
    }

    public isCulturalActivity(type: IActivityEntityType): type is CulturalActivity {
        return type instanceof CulturalActivity;
    }

    public isItem(type: IActivityEntityType): type is Item {
        return type instanceof Item;
    }

    public isItemGroup(group: IActivityGroup | undefined, type: ActivityType) {
        if (type === NonCulturalActivityType.Actions) {
            return group as IActivityGroup<Item>;
        }

        return undefined;
    }

    public isObjectiveGroup(group: IActivityGroup | undefined, type: ActivityType) {
        if (type === NonCulturalActivityType.Objectives) {
            return group as IActivityGroup<Objective>;
        }

        return undefined;
    }

    public isMeetingGroup(group: IActivityGroup | undefined, type: ActivityType) {
        if (type === NonCulturalActivityType.Meetings) {
            return group as IActivityGroup<Meeting>;
        }

        return undefined;
    }

    public isMeeting(type: IActivityEntityType): type is Meeting {
        return type instanceof Meeting;
    }

    public isObjective(type: IActivityEntityType): type is Objective {
        return type instanceof Objective;
    }

    public isObjectiveTeamGroup(type: IActivityEntityType): type is IObjectiveTeamGroup {
        return "objectives" in type && "team" in type;
    }

    public isMeetingTeamGroup(type: IActivityEntityType): type is IMeetingTeamGroup {
        return "meetings" in type && "team" in type;
    }

    public asStatusGroupRecord(group: KeyValue<string, IActivityEntityType[]>[] | null) {
        if (!group) {
            return group;
        }

        return group as ICulturalActivityStatusGroupKeyValueRecord[];
    }

    @AfterOrganisationInitialisation
    public async getActivitiesGroupedByType(typesToUpdate: ActivityType[], startDate?: Date, endDate?: Date) {
        const groups = {} as Partial<IActivitiesGroupedByActivityType>;

        const personId = this.userService.currentPerson?.personId;
        if (!personId) {
            return groups;
        }

        const dueWithinDate = this.activityPageService.filter?.dueWithin
            // add an extra day for culturalActivities on the boundary to show as well
            ? moment(DurationSelector.getTimeInFuture(this.activityPageService.filter.dueWithin)).add(1, "day").toDate()
            : undefined;
        const requestEndDate = endDate ?? dueWithinDate;

        // only show completed events on the calendar view
        const isCalendarView = this.activityPageService.page === ActivityPage.Calendar;

        // only attempt to update types that are accessible
        // this will have checked permissions already
        typesToUpdate = ArrayUtilities.intersectArrays(typesToUpdate ?? CulturalActivityTypes, this.accessibleActivityTypes ?? CulturalActivityTypes);
        const promises = this.activityHandlers.map(async (activityHandler) => {
            // check if the handler can fetch any of these activity types
            const typesToHandle = ArrayUtilities.intersectArrays(typesToUpdate, activityHandler.activityTypes);
            if (typesToHandle.length > 0) {
                try {
                    const activityGroups = await activityHandler.getGroupedActivities(personId, startDate, requestEndDate, !isCalendarView, typesToHandle);
                    for (const type of typesToHandle) {
                        const group = activityGroups[type];
                        if (group) {
                            groups[type] = group;
                        }
                    }
                    // reset errors for this type when successful fetch occurs
                    this.activityTypesError.next({
                        types: typesToHandle,
                        error: undefined,
                    });
                } catch (e) {
                    this.activityTypesError.next({
                        types: typesToHandle,
                        error: ErrorHandlingUtilities.getHttpResponseMessage(e),
                    });
                } finally {
                    this.activityTypesLoaded.next(typesToHandle);
                }
            }
        });
        await Promise.all(promises);

        return groups;
    }

    public groupActivitiesByStatus(typeGroupedActivities: IActivitiesGroupedByActivityType) {
        const statusGroups = this.generateActivitiesGroupedByStatusObject();
        const totalCounts = ActivitiesService.generateActivitiesGroupedByStatusCountObject();
        const displayedCounts = ActivitiesService.generateActivitiesGroupedByStatusCountObject();

        for (const { type } of CulturalActivityTypeMetadata) {
            const group = typeGroupedActivities[type];

            // capture the counts before we group objectives/meetings
            // otherwise it'll be a count of groups rather than individual items
            const overdueCount = group[CulturalActivityStatus.Overdue].length;
            const upcomingCount = group[CulturalActivityStatus.Upcoming].length;

            const handler = this.activityHandlers.find((h) => h.activityTypes.includes(type));
            const { overdue, upcoming } = handler?.groupActivitiesByStatus(group) ?? group;

            // update the total count for the status group
            totalCounts[CulturalActivityStatus.Overdue] += group.totalCountByStatus[CulturalActivityStatus.Overdue];
            totalCounts[CulturalActivityStatus.Upcoming] += group.totalCountByStatus[CulturalActivityStatus.Upcoming];

            // count of displayed activities
            displayedCounts[CulturalActivityStatus.Overdue] += overdueCount;
            displayedCounts[CulturalActivityStatus.Upcoming] += upcomingCount;

            // update the status group activities for each type
            statusGroups[CulturalActivityStatus.Overdue][type] = overdue;
            statusGroups[CulturalActivityStatus.Upcoming][type] = upcoming;
        }

        return { statusGroups, displayedCounts, totalCounts };
    }

    public async promiseToGetCalendarEventForActivity(activity: IActivityEntityType): Promise<ICalendarCulturalActivity> {
        const type = ActivitiesService.getActivityType(activity);
        const metadata = CulturalActivityMetadataByType[type];

        const handler = this.activityHandlers.find((h) => h.activityTypes.includes(type));
        if (!handler) {
            throw new Error(`No handler found for activity type ${type}`);
        }

        const { title, startDate, endDate, completed } = await handler.getCalendarEventForActivity(activity);
        return { activity, title, startDate, endDate, isAllDay: !endDate, type, metadata, completed };
    }

    protected organisationInitialisationActions() {
        return [
            this.initialiseService(),
        ];
    }

    private async initialiseService() {
        // filter out sections without feature/access
        const accessibleActivityTypes = await ArrayUtilities.asyncFilter(CulturalActivityTypeMetadata, async (type) => {
            return this.featuresService.isFeatureActive(type.feature)
                && (!type.accessVerifier || await this.authorisationService.promiseToGetHasAccess(type.accessVerifier));
        });

        this.accessibleActivityTypes = accessibleActivityTypes.map(({ type }) => type);
    }

    private generateActivitiesGroupedByStatusObject() {
        return CulturalActivityTypeMetadata.reduce((groups, { type }) => {
            groups[CulturalActivityStatus.Overdue][type] = [];
            groups[CulturalActivityStatus.Upcoming][type] = [];
            return groups;
        }, {
            [CulturalActivityStatus.Overdue]: {},
            [CulturalActivityStatus.Upcoming]: {},
        } as ICulturalActivitiesGroupedByStatusAndType);
    }
}
